import { Controller } from "@hotwired/stimulus";
import Decimal from "decimal.js";

import { appCurrency } from "../utils/currency";
import {
  beginBudgetAllocationEdit,
  beginBudgetAllocationUndo,
  budgetAllocationEditMatches,
  completeBudgetAllocationEdit,
  type BudgetAllocationEdit,
} from "../utils/graphs/budget_allocation_edit";
import {
  allocationSummaryFor,
  availableForBudgeted,
  type BudgetAllocationSegment,
  type BudgetAllocationState,
  clampBudgeted,
  decimalFromRaw,
  keyboardStep,
  maxAvailable,
  minAvailable,
  overspentForAvailable,
  percent,
  segmentsFor,
} from "../utils/graphs/budget_allocation";

interface CurrencyInputElement extends HTMLElement {
  value: string;
}

interface TurboFormSubmissionEventDetail {
  formSubmission?: {
    formElement?: HTMLFormElement;
  };
  success?: boolean;
}

const editFeedbackDurationMs = 10_000;
const allocationEdits = new Map<string, BudgetAllocationEdit>();
// Preserve user action order when Turbo responses complete out of order.
const allocationEditSequences = new Map<string, number>();
let nextAllocationEditSequence = 0;
// Track only the most recent save; the shortcut deliberately does not form an undo history.
let latestUndoableEditKey: string | undefined;
let latestUndoableEditSequence = 0;

function setAllocationEdit(
  editKey: string,
  edit: BudgetAllocationEdit,
): void {
  if (edit.status === "saving") {
    nextAllocationEditSequence += 1;
    allocationEditSequences.set(editKey, nextAllocationEditSequence);
  }

  allocationEdits.set(editKey, edit);
  const editSequence = allocationEditSequences.get(editKey) ?? 0;
  if (
    edit.status === "saved" &&
    editSequence >= latestUndoableEditSequence
  ) {
    latestUndoableEditKey = editKey;
    latestUndoableEditSequence = editSequence;
  } else if (latestUndoableEditKey === editKey) {
    latestUndoableEditKey = undefined;
    latestUndoableEditSequence = Math.max(
      latestUndoableEditSequence,
      editSequence,
    );
  }
}

function deleteAllocationEdit(editKey: string): void {
  const editSequence = allocationEditSequences.get(editKey) ?? 0;
  allocationEdits.delete(editKey);
  allocationEditSequences.delete(editKey);
  if (latestUndoableEditKey === editKey) {
    latestUndoableEditKey = undefined;
    latestUndoableEditSequence = Math.max(
      latestUndoableEditSequence,
      editSequence,
    );
  }
}

const textEditingInputTypes = new Set([
  "date",
  "datetime-local",
  "email",
  "month",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "time",
  "url",
  "week",
]);

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  const input = target.closest("input");
  if (input) {
    return textEditingInputTypes.has(input.type);
  }

  return (
    target.closest(
      "textarea, [contenteditable]:not([contenteditable='false'])",
    ) !== null
  );
}

export default class extends Controller<HTMLElement> {
  static targets = [
    "svg",
    "activityBar",
    "availableBar",
    "overspentBar",
    "handle",
    "handleHitArea",
    "preview",
    "budgetedLabel",
    "monthlyAssignmentLabel",
    "availableBreakdownLabel",
    "feedback",
    "status",
    "cancel",
    "undo",
  ];

  static values = {
    categoryId: String,
    scale: String,
    balance: String,
    usage: String,
    funding: String,
    available: String,
    budgeted: String,
    monthlyAssignment: String,
    readyToAssign: String,
    totalAvailable: String,
    totalAllocation: String,
    categoryOverspending: String,
    editable: Boolean,
    debounceMs: { type: Number, default: 700 },
  };

  declare readonly svgTarget: SVGSVGElement;
  declare readonly activityBarTarget: SVGRectElement;
  declare readonly availableBarTarget: SVGRectElement;
  declare readonly overspentBarTarget: SVGRectElement;
  declare readonly handleTarget: SVGLineElement;
  declare readonly handleHitAreaTarget: SVGCircleElement;
  declare readonly previewTarget: HTMLElement;
  declare readonly budgetedLabelTarget: HTMLElement;
  declare readonly monthlyAssignmentLabelTarget: HTMLElement;
  declare readonly availableBreakdownLabelTarget: HTMLElement;
  declare readonly feedbackTarget: HTMLElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly cancelTarget: HTMLButtonElement;
  declare readonly undoTarget: HTMLButtonElement;

  declare readonly hasSvgTarget: boolean;
  declare readonly hasPreviewTarget: boolean;
  declare readonly hasBudgetedLabelTarget: boolean;
  declare readonly hasMonthlyAssignmentLabelTarget: boolean;
  declare readonly hasAvailableBreakdownLabelTarget: boolean;
  declare readonly hasFeedbackTarget: boolean;
  declare readonly hasStatusTarget: boolean;
  declare readonly hasCancelTarget: boolean;
  declare readonly hasUndoTarget: boolean;

  declare readonly categoryIdValue: string;
  declare readonly scaleValue: string;
  declare readonly balanceValue: string;
  declare readonly usageValue: string;
  declare readonly fundingValue: string;
  declare readonly availableValue: string;
  declare readonly budgetedValue: string;
  declare readonly monthlyAssignmentValue: string;
  declare readonly readyToAssignValue: string;
  declare readonly totalAvailableValue: string;
  declare readonly totalAllocationValue: string;
  declare readonly categoryOverspendingValue: string;
  declare readonly editableValue: boolean;
  declare readonly debounceMsValue: number;

  private commitTimer: number | undefined;
  private feedbackTimer: number | undefined;
  private draggingPointerId: number | null = null;
  private previewAvailable: Decimal | null = null;
  private previewBudgeted: Decimal | null = null;
  private resizeObserver: ResizeObserver | undefined;
  private form: HTMLFormElement | null = null;

  private readonly onTurboSubmitStart = (event: Event): void => {
    this.handleTurboSubmitStart(event);
  };

  private readonly onTurboSubmitEnd = (event: Event): void => {
    this.handleTurboSubmitEnd(event);
  };

  private readonly onUndoShortcut = (event: KeyboardEvent): void => {
    if (
      event.defaultPrevented ||
      event.isComposing ||
      event.altKey ||
      event.shiftKey ||
      !(event.metaKey || event.ctrlKey) ||
      event.key.toLowerCase() !== "z" ||
      isTextEditingTarget(event.target) ||
      latestUndoableEditKey !== this.editKey ||
      allocationEdits.get(this.editKey)?.status !== "saved" ||
      !this.element.isConnected ||
      !this.hasUndoTarget ||
      this.undoTarget.hidden ||
      this.undoTarget.disabled
    ) {
      return;
    }

    event.preventDefault();
    this.undoTarget.click();
  };

  private readonly onExactInput = (): void => {
    const edit = allocationEdits.get(this.editKey);
    if (edit?.status === "saving") {
      return;
    }

    this.clearCommitTimer();
    const hadPreview = this.previewAvailable !== null;
    this.previewAvailable = null;
    this.previewBudgeted = null;
    if (hadPreview) {
      this.render(this.budgeted, this.available, false);
    }
    this.discardEditFeedback();
  };

  private readonly onExactChange = (event: Event): void => {
    const input = this.currencyInput();
    if (
      !input ||
      event.target !== input ||
      !this.form?.checkValidity() ||
      allocationEdits.get(this.editKey)?.status === "saving"
    ) {
      return;
    }

    const requestedAvailable = decimalFromRaw(input.value);
    if (requestedAvailable.eq(this.available)) {
      return;
    }

    const edit = beginBudgetAllocationEdit(
      this.available.toString(),
      requestedAvailable.toString(),
      this.monthlyAssignment.toString(),
    );
    setAllocationEdit(this.editKey, edit);
    this.previewAvailable = requestedAvailable;
    this.previewBudgeted = requestedAvailable.add(this.usage);
    this.render(this.previewBudgeted, this.previewAvailable, true);
    this.showEditFeedback(edit);
  };

  connect(): void {
    this.form = this.element.closest("form");
    this.revealCompactedRowForFeedback();
    document.addEventListener("turbo:submit-start", this.onTurboSubmitStart);
    document.addEventListener("turbo:submit-end", this.onTurboSubmitEnd);
    document.addEventListener("keydown", this.onUndoShortcut);
    this.innerCurrencyInput()?.addEventListener("input", this.onExactInput);
    this.currencyInput()?.addEventListener("change", this.onExactChange);

    this.syncViewBoxToRenderedWidth();
    this.resizeObserver = new ResizeObserver(() =>
      this.syncViewBoxToRenderedWidth(),
    );
    this.resizeObserver.observe(this.svgTarget);
    this.render(this.budgeted, this.available, false);
    this.restoreEditFeedback();
  }

  disconnect(): void {
    this.clearCommitTimer();
    this.resizeObserver?.disconnect();
    document.removeEventListener("turbo:submit-start", this.onTurboSubmitStart);
    document.removeEventListener("turbo:submit-end", this.onTurboSubmitEnd);
    document.removeEventListener("keydown", this.onUndoShortcut);
    this.innerCurrencyInput()?.removeEventListener("input", this.onExactInput);
    this.currencyInput()?.removeEventListener("change", this.onExactChange);
    this.form = null;
  }

  startDrag(event: PointerEvent): void {
    if (!this.canEdit()) {
      return;
    }

    event.preventDefault();
    this.clearCommitTimer();
    this.draggingPointerId = event.pointerId;
    this.svgTarget.setPointerCapture(event.pointerId);
    this.updateFromPointer(event);
  }

  drag(event: PointerEvent): void {
    if (this.draggingPointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    this.updateFromPointer(event);
  }

  endDrag(event: PointerEvent): void {
    if (this.draggingPointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    this.finishPointerCapture(event.pointerId);
    this.scheduleCommit();
  }

  cancelDrag(event: PointerEvent): void {
    if (this.draggingPointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    this.finishPointerCapture(event.pointerId);
    this.clearPreview();
  }

  cancelPreview(event: Event): void {
    event.preventDefault();
    if (this.element.dataset.budgetAllocationStatus === "saving") {
      return;
    }

    this.clearCommitTimer();
    this.clearPreview();
    this.svgTarget.focus();
  }

  undo(event: Event): void {
    event.preventDefault();
    const savedEdit = allocationEdits.get(this.editKey);
    if (savedEdit?.status !== "saved" || !this.canSubmitEdit()) {
      return;
    }

    const input = this.currencyInput();
    if (!input) {
      this.discardEditFeedback();
      return;
    }

    const undoEdit = beginBudgetAllocationUndo(savedEdit);
    const nextAvailable = decimalFromRaw(undoEdit.requestedAvailable);
    const nextBudgeted = nextAvailable.add(this.usage);

    this.previewAvailable = nextAvailable;
    this.previewBudgeted = nextBudgeted;
    this.render(nextBudgeted, nextAvailable, true);
    this.submitEdit(input, undoEdit);
  }

  adjustWithKeyboard(event: KeyboardEvent): void {
    if (!this.canEdit()) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.clearCommitTimer();
      this.clearPreview();
      return;
    }

    const step = keyboardStep(event.key, this.state, this.currentBudgeted());
    if (step.isZero()) {
      return;
    }

    event.preventDefault();
    const nextBudgeted = clampBudgeted(
      this.currentBudgeted().add(step),
      this.state,
    );
    this.updatePreview(nextBudgeted);
    this.scheduleCommit();
  }

  private finishPointerCapture(pointerId: number): void {
    this.draggingPointerId = null;
    if (this.svgTarget.hasPointerCapture(pointerId)) {
      this.svgTarget.releasePointerCapture(pointerId);
    }
  }

  private syncViewBoxToRenderedWidth(): void {
    if (!this.hasSvgTarget) {
      return;
    }

    const width = Math.max(
      Math.round(this.svgTarget.getBoundingClientRect().width),
      100,
    );
    this.svgTarget.setAttribute("viewBox", `0 0 ${width} 44`);
  }

  private updateFromPointer(event: PointerEvent): void {
    const bounds = this.svgTarget.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }

    const ratio = Math.min(
      Math.max((event.clientX - bounds.left) / bounds.width, 0),
      1,
    );
    const nextBudgeted = this.scale.mul(ratio).toDecimalPlaces(2);
    this.updatePreview(nextBudgeted);
  }

  private updatePreview(budgeted: Decimal): void {
    this.discardEditFeedback();

    const nextBudgeted = clampBudgeted(budgeted, this.state);
    const nextAvailable = availableForBudgeted(nextBudgeted, this.usage);

    this.previewBudgeted = nextBudgeted;
    this.previewAvailable = nextAvailable;
    this.render(nextBudgeted, nextAvailable, true);
    this.showFeedback("previewing", "Previewing Available change.", true, false);
  }

  private scheduleCommit(): void {
    if (!this.previewAvailable) {
      return;
    }

    this.clearCommitTimer();
    this.commitTimer = window.setTimeout(
      () => this.commitPreview(),
      this.debounceMsValue,
    );
  }

  private commitPreview(): void {
    this.clearCommitTimer();
    if (!this.previewAvailable || !this.previewBudgeted) {
      return;
    }

    const input = this.currencyInput();
    if (!input) {
      this.clearPreview();
      return;
    }

    const nextValue = this.previewAvailable.toDecimalPlaces(2).toString();
    if (decimalFromRaw(input.value).eq(this.previewAvailable)) {
      this.clearPreview();
      return;
    }

    const edit = beginBudgetAllocationEdit(
      this.available.toString(),
      nextValue,
      this.monthlyAssignment.toString(),
    );
    this.submitEdit(input, edit);
  }

  private submitEdit(
    input: CurrencyInputElement,
    edit: BudgetAllocationEdit,
  ): void {
    setAllocationEdit(this.editKey, edit);
    input.value = edit.requestedAvailable;
    this.showEditFeedback(edit);

    if (edit.kind === "undo" && this.form) {
      const assignment = document.createElement("input");
      assignment.type = "hidden";
      assignment.name = "restore_assignment";
      assignment.value = edit.previousMonthlyAssignment;
      this.form.append(assignment);
      // Submit even if Available did not change: funding can map multiple
      // assignments to the same Available amount.
      this.form.requestSubmit();
      const innerInput = this.innerCurrencyInput();
      if (innerInput) {
        innerInput.disabled = true;
      }
      return;
    }

    // currency-input forwards data-action to its inner input. Dispatch the
    // change there so the existing forms controller submits through the normal path.
    const changeTarget = this.innerCurrencyInput() || input;
    changeTarget.dispatchEvent(
      new Event("change", { bubbles: true, cancelable: true }),
    );
  }

  private clearPreview(): void {
    this.previewAvailable = null;
    this.previewBudgeted = null;
    this.discardEditFeedback();
    this.render(this.budgeted, this.available, false);
  }

  private render(
    budgeted: Decimal,
    available: Decimal,
    previewing: boolean,
  ): void {
    const segments = segmentsFor(this.state, available, budgeted);

    this.setRect(this.activityBarTarget, segments.activity);
    this.setRect(this.availableBarTarget, segments.available);
    this.setRect(this.overspentBarTarget, segments.overspent);

    const budgetedPercent = this.percent(budgeted);
    this.handleTarget.setAttribute("x1", `${budgetedPercent}%`);
    this.handleTarget.setAttribute("x2", `${budgetedPercent}%`);
    this.handleHitAreaTarget.setAttribute("cx", `${budgetedPercent}%`);

    this.renderLabels(budgeted, available, previewing);
    this.renderStateClasses(available, previewing);
    this.renderAria(budgeted, available);
    this.renderGlobalSummary(budgeted, available);
  }

  private renderLabels(
    budgeted: Decimal,
    available: Decimal,
    previewing: boolean,
  ): void {
    if (this.hasBudgetedLabelTarget) {
      this.budgetedLabelTarget.textContent = this.format(budgeted);
    }
    if (this.hasMonthlyAssignmentLabelTarget) {
      const monthlyAssignment = this.monthlyAssignment.add(
        budgeted.sub(this.budgeted),
      );
      this.monthlyAssignmentLabelTarget.textContent =
        this.format(monthlyAssignment);
    }
    if (this.hasAvailableBreakdownLabelTarget) {
      this.availableBreakdownLabelTarget.textContent = this.format(available);
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.textContent = previewing
        ? `Available: ${this.format(available)}`
        : "";
    }
  }

  private renderStateClasses(available: Decimal, previewing: boolean): void {
    this.element.classList.toggle("is-previewing", previewing);
    this.element.classList.toggle("is-over-budget", available.lt(0));
  }

  private renderAria(budgeted: Decimal, available: Decimal): void {
    if (!this.hasSvgTarget || this.svgTarget.getAttribute("role") !== "slider") {
      return;
    }

    const minimumAvailable = Decimal.min(minAvailable(this.state), available);
    const maximumAvailable = Decimal.max(maxAvailable(this.state), available);
    const overspent = overspentForAvailable(available);
    const overspentText = overspent.gt(0)
      ? `, overspent by ${this.format(overspent)}`
      : "";

    this.svgTarget.setAttribute(
      "aria-valuemin",
      minimumAvailable.toDecimalPlaces(2).toString(),
    );
    this.svgTarget.setAttribute(
      "aria-valuemax",
      maximumAvailable.toDecimalPlaces(2).toString(),
    );
    this.svgTarget.setAttribute(
      "aria-valuenow",
      available.toDecimalPlaces(2).toString(),
    );
    this.svgTarget.setAttribute(
      "aria-valuetext",
      `Available ${this.format(available)}, activity ${this.format(
        this.usage.add(decimalFromRaw(this.fundingValue)),
      )}, envelope ${this.format(budgeted)}${
        decimalFromRaw(this.fundingValue).isZero()
          ? ""
          : `, net funding from other envelopes ${this.format(decimalFromRaw(this.fundingValue))}`
      }${overspentText}`,
    );
  }

  private renderGlobalSummary(
    budgeted: Decimal,
    available: Decimal,
  ): void {
    const period = this.element.closest("turbo-frame#budgeting-budget");
    const summary =
      period?.querySelector<HTMLElement>("[data-budget-summary]") ||
      document.querySelector<HTMLElement>("[data-budget-summary]");
    const label = summary?.querySelector<HTMLElement>(
      "[data-budget-summary-label]",
    );
    const value = summary?.querySelector<HTMLElement>(
      "[data-budget-summary-value]",
    );
    if (!summary || !label || !value) {
      return;
    }

    const values = allocationSummaryFor(available, budgeted, this.state);
    summary.dataset.budgetSummaryState = values.state;
    label.textContent =
      values.state === "over-assigned"
        ? "Over-assigned"
        : values.state === "ready"
          ? "Ready to assign"
          : "Fully assigned";
    value.textContent = this.format(values.primaryAmount);

    const totalAllocation = summary.querySelector<HTMLElement>(
      "[data-budget-summary-total-allocation-value]",
    );
    if (totalAllocation) {
      totalAllocation.textContent = this.format(values.totalAllocation);
    }

    const cashShortageMetric = summary.querySelector<HTMLElement>(
      "[data-budget-summary-cash-shortage-metric]",
    );
    const cashShortage = summary.querySelector<HTMLElement>(
      "[data-budget-summary-cash-shortage-value]",
    );
    if (cashShortageMetric) {
      cashShortageMetric.hidden = !values.cashShortage.gt(0);
    }
    if (cashShortage) {
      cashShortage.textContent = this.format(values.cashShortage);
    }

    const balance = summary.querySelector<HTMLElement>(
      "[data-budget-summary-balance-value]",
    );
    if (balance) {
      balance.textContent = this.format(values.balance);
    }

    const categoryOverspendingMetric = summary.querySelector<HTMLElement>(
      "[data-budget-summary-category-overspending-metric]",
    );
    const categoryOverspending = summary.querySelector<HTMLElement>(
      "[data-budget-summary-category-overspending-value]",
    );
    if (categoryOverspendingMetric) {
      categoryOverspendingMetric.hidden = !values.categoryOverspending.gt(0);
    }
    if (categoryOverspending) {
      categoryOverspending.textContent = this.format(
        values.categoryOverspending,
      );
    }
  }

  private handleTurboSubmitStart(event: Event): void {
    const form = this.submittedForm(event);
    if (!form || this.editKeyFor(form) !== this.editKey) {
      return;
    }

    const edit = allocationEdits.get(this.editKey);
    if (edit?.status !== "saving") {
      this.discardEditFeedback();
    }
  }

  private handleTurboSubmitEnd(event: Event): void {
    const form = this.submittedForm(event);
    if (!form || this.editKeyFor(form) !== this.editKey) {
      return;
    }
    form.querySelector('input[name="restore_assignment"]')?.remove();

    const edit = allocationEdits.get(this.editKey);
    if (edit?.status !== "saving") {
      return;
    }

    const success =
      (event as CustomEvent<TurboFormSubmissionEventDetail>).detail?.success ===
      true;
    const completedEdit = completeBudgetAllocationEdit(edit, success);
    setAllocationEdit(this.editKey, completedEdit);

    if (success) {
      this.showEditFeedback(completedEdit);
      return;
    }

    this.restoreAfterFailure(completedEdit, true);
  }

  private revealCompactedRowForFeedback(): void {
    if (!allocationEdits.has(this.editKey)) {
      return;
    }

    const disclosure = this.element.closest<HTMLDetailsElement>(
      "details[data-budget-category-unfunded]",
    );
    if (disclosure) {
      disclosure.open = true;
    }
  }

  private restoreEditFeedback(): void {
    const edit = allocationEdits.get(this.editKey);
    if (!edit) {
      return;
    }

    if (edit.status === "saving") {
      if (
        budgetAllocationEditMatches(edit, this.availableValue, this.monthlyAssignmentValue)
      ) {
        const completedEdit = completeBudgetAllocationEdit(edit, true);
        setAllocationEdit(this.editKey, completedEdit);
        this.showEditFeedback(completedEdit);
        return;
      }

      this.showEditFeedback(edit);
      return;
    }

    if (
      edit.status !== "failed" &&
      !budgetAllocationEditMatches(edit, this.availableValue, this.monthlyAssignmentValue)
    ) {
      const failedEdit = completeBudgetAllocationEdit(edit, false);
      setAllocationEdit(this.editKey, failedEdit);
      this.restoreAfterFailure(failedEdit, false);
      return;
    }

    this.showEditFeedback(edit);
  }

  private restoreAfterFailure(
    edit: BudgetAllocationEdit,
    restoreInput: boolean,
  ): void {
    this.clearCommitTimer();
    this.previewAvailable = null;
    this.previewBudgeted = null;

    if (restoreInput) {
      const input = this.currencyInput();
      if (input) {
        input.value = edit.previousAvailable;
      }
      const innerInput = this.innerCurrencyInput();
      if (innerInput) {
        innerInput.disabled = false;
      }
    }

    this.render(this.budgeted, this.available, false);
    this.showEditFeedback(edit);
  }

  private showEditFeedback(edit: BudgetAllocationEdit): void {
    switch (edit.status) {
      case "saving":
        this.showFeedback("saving", "Saving…", false, false);
        return;
      case "saved":
        this.showFeedback("saved", "Saved.", false, true);
        this.scheduleFeedbackDismissal(edit);
        return;
      case "failed":
        this.showFeedback(
          "failed",
          "Failed — previous Available restored.",
          false,
          false,
        );
        this.scheduleFeedbackDismissal(edit);
        return;
      case "restored":
        this.showFeedback(
          "restored",
          "Restored previous Available.",
          false,
          false,
        );
        this.scheduleFeedbackDismissal(edit);
    }
  }

  private showFeedback(
    status: string,
    copy: string,
    cancellable: boolean,
    undoable: boolean,
  ): void {
    this.element.dataset.budgetAllocationStatus = status;
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.hidden = false;
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = copy;
    }
    if (this.hasCancelTarget) {
      this.cancelTarget.hidden = !cancellable;
    }
    if (this.hasUndoTarget) {
      this.undoTarget.hidden = !undoable;
    }
  }

  private scheduleFeedbackDismissal(edit: BudgetAllocationEdit): void {
    this.clearFeedbackTimer();
    const editKey = this.editKey;
    this.feedbackTimer = window.setTimeout(() => {
      this.feedbackTimer = undefined;
      const currentEdit = allocationEdits.get(editKey);
      if (currentEdit && currentEdit !== edit) {
        return;
      }
      if (currentEdit === edit) {
        deleteAllocationEdit(editKey);
      }
      if (this.element.dataset.budgetAllocationStatus === edit.status) {
        this.hideFeedback();
      }
    }, editFeedbackDurationMs);
  }

  private discardEditFeedback(): void {
    this.clearFeedbackTimer();
    deleteAllocationEdit(this.editKey);
    this.hideFeedback();
  }

  private hideFeedback(): void {
    delete this.element.dataset.budgetAllocationStatus;
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.hidden = true;
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "";
    }
    if (this.hasCancelTarget) {
      this.cancelTarget.hidden = true;
    }
    if (this.hasUndoTarget) {
      this.undoTarget.hidden = true;
    }
  }

  private submittedForm(event: Event): HTMLFormElement | null {
    return (
      (event as CustomEvent<TurboFormSubmissionEventDetail>).detail
        ?.formSubmission?.formElement || null
    );
  }

  private editKeyFor(form: HTMLFormElement): string {
    const categoryId =
      form.dataset.budgetAllocationCategoryId ||
      form
        .querySelector<HTMLElement>("[data-budget-allocation-bar-category-id-value]")
        ?.dataset.budgetAllocationBarCategoryIdValue ||
      "";
    return `${form.action}::${categoryId}`;
  }

  private setRect(
    rect: SVGRectElement,
    segment: BudgetAllocationSegment,
  ): void {
    rect.setAttribute("x", `${this.percent(segment.x)}%`);
    rect.setAttribute("width", `${this.percent(segment.width)}%`);
  }

  private currentBudgeted(): Decimal {
    return this.previewBudgeted || this.budgeted;
  }

  private percent(value: Decimal): number {
    return percent(value, this.scale);
  }

  private canEdit(): boolean {
    return (
      this.editableValue &&
      this.scale.gt(0) &&
      this.element.dataset.budgetAllocationStatus !== "saving"
    );
  }

  private canSubmitEdit(): boolean {
    return (
      this.form !== null &&
      this.currencyInput() !== null &&
      this.element.dataset.budgetAllocationStatus !== "saving"
    );
  }

  private currencyInput(): CurrencyInputElement | null {
    return (
      this.form?.querySelector<CurrencyInputElement>(
        "currency-input[name='amount']",
      ) || null
    );
  }

  private innerCurrencyInput(): HTMLInputElement | null {
    return this.currencyInput()?.querySelector<HTMLInputElement>("input") || null;
  }

  private clearCommitTimer(): void {
    if (this.commitTimer === undefined) {
      return;
    }

    window.clearTimeout(this.commitTimer);
    this.commitTimer = undefined;
  }

  private clearFeedbackTimer(): void {
    if (this.feedbackTimer === undefined) {
      return;
    }

    window.clearTimeout(this.feedbackTimer);
    this.feedbackTimer = undefined;
  }

  private format(value: Decimal): string {
    return appCurrency(value.toNumber()).format();
  }

  private get editKey(): string {
    if (!this.form) {
      return `::${this.categoryIdValue}`;
    }

    return this.editKeyFor(this.form);
  }

  private get state(): BudgetAllocationState {
    return {
      scale: this.scale,
      balance: this.balance,
      usage: this.usage,
      available: this.available,
      budgeted: this.budgeted,
      readyToAssign: this.readyToAssign,
      totalAvailable: this.totalAvailable,
      totalAllocation: this.totalAllocation,
      categoryOverspending: this.categoryOverspending,
    };
  }

  private get scale(): Decimal {
    return decimalFromRaw(this.scaleValue);
  }

  private get balance(): Decimal {
    return decimalFromRaw(this.balanceValue);
  }

  private get usage(): Decimal {
    return decimalFromRaw(this.usageValue);
  }

  private get available(): Decimal {
    return decimalFromRaw(this.availableValue);
  }

  private get monthlyAssignment(): Decimal {
    return decimalFromRaw(this.monthlyAssignmentValue);
  }

  private get budgeted(): Decimal {
    return decimalFromRaw(this.budgetedValue);
  }

  private get readyToAssign(): Decimal {
    return decimalFromRaw(this.readyToAssignValue);
  }

  private get totalAvailable(): Decimal {
    return decimalFromRaw(this.totalAvailableValue);
  }

  private get totalAllocation(): Decimal {
    return decimalFromRaw(this.totalAllocationValue);
  }

  private get categoryOverspending(): Decimal {
    return decimalFromRaw(this.categoryOverspendingValue);
  }
}
